import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import { format, parseISO, isWithinInterval, addDays, isSameDay, differenceInDays } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { Droplets, Plus, Calendar as CalendarIcon, Heart, Sparkles, Clock, AlertCircle } from 'lucide-react';
import { useCycle } from '../context/CycleContext';
import CycleLogForm from '../components/cycle/CycleLogForm';
import 'react-calendar/dist/Calendar.css';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

const CalendarView: React.FC = () => {
  const { t } = useTranslation();
  const { logs, stats } = useCycle();
  const [value, setValue] = useState<Value>(new Date());
  const [showLogForm, setShowLogForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedLog, setSelectedLog] = useState<number | null>(null);

  // Helper function to predict period days with enhanced accuracy
  const getPredictedPeriodDates = () => {
    if (!stats.nextPeriodPrediction) return [];
    
    const startDate = parseISO(stats.nextPeriodPrediction);
    const periodDays: Date[] = [];
    
    // Predict 5 days of period (can be customized based on user's average)
    for (let i = 0; i < 5; i++) {
      periodDays.push(addDays(startDate, i));
    }
    
    return periodDays;
  };

  // Helper function to get ovulation prediction
  const getOvulationPrediction = () => {
    if (!stats.lastPeriodDate) return null;
    
    const lastPeriod = parseISO(stats.lastPeriodDate);
    const ovulationDay = Math.round(stats.averageCycleLength / 2);
    return addDays(lastPeriod, ovulationDay);
  };

  // Helper function to get fertile window
  const getFertileWindow = () => {
    const ovulationDate = getOvulationPrediction();
    if (!ovulationDate) return [];
    
    const fertileWindow: Date[] = [];
    // Fertile window: 5 days before ovulation + ovulation day + 1 day after
    for (let i = -5; i <= 1; i++) {
      fertileWindow.push(addDays(ovulationDate, i));
    }
    
    return fertileWindow;
  };

  const predictedPeriodDates = getPredictedPeriodDates();
  const ovulationDate = getOvulationPrediction();
  const fertileWindow = getFertileWindow();
  
  const getLogForDate = (date: Date) => {
    return logs.find(log => isSameDay(parseISO(log.date), date));
  };

  const handleDateClick = (date: Date) => {
    const log = getLogForDate(date);
    
    setSelectedDate(date);
    if (log) {
      setSelectedLog(log.id);
    } else {
      setSelectedLog(null);
    }
    setShowLogForm(true);
  };

  const handleCloseForm = () => {
    setShowLogForm(false);
    setSelectedLog(null);
    setSelectedDate(null);
  };

  const isStartOfPeriod = (date: Date) => {
    return logs.some(log => 
      log.period_start && isSameDay(parseISO(log.date), date)
    );
  };
  
  const isInPeriod = (date: Date) => {
    // Find all period start dates
    const periodStartDates = logs
      .filter(log => log.period_start)
      .map(log => parseISO(log.date));
    
    // Check if date is within 5 days of any period start date
    return periodStartDates.some(startDate => 
      isWithinInterval(date, {
        start: startDate,
        end: addDays(startDate, 4) // Assuming 5 days of period
      })
    );
  };
  
  const isPredictedPeriod = (date: Date) => {
    return predictedPeriodDates.some(predictedDate => 
      isSameDay(predictedDate, date)
    );
  };

  const isPredictedPeriodStart = (date: Date) => {
    return stats.nextPeriodPrediction && isSameDay(parseISO(stats.nextPeriodPrediction), date);
  };

  const isOvulationDay = (date: Date) => {
    return ovulationDate && isSameDay(ovulationDate, date);
  };

  const isInFertileWindow = (date: Date) => {
    return fertileWindow.some(fertileDate => isSameDay(fertileDate, date));
  };

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    
    const classes = [];
    
    if (isStartOfPeriod(date)) {
      classes.push('period-start');
    } else if (isInPeriod(date)) {
      classes.push('period-day');
    } else if (isPredictedPeriodStart(date)) {
      classes.push('predicted-period-start');
    } else if (isPredictedPeriod(date)) {
      classes.push('predicted-period');
    } else if (isOvulationDay(date)) {
      classes.push('ovulation-day');
    } else if (isInFertileWindow(date)) {
      classes.push('fertile-window');
    }
    
    const log = getLogForDate(date);
    if (log) {
      classes.push('has-log');
    }
    
    return classes.join(' ');
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null;
    
    // Period start (actual)
    if (isStartOfPeriod(date)) {
      return (
        <motion.div
          className="flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Droplets className="h-4 w-4 text-white fill-pink-500" />
        </motion.div>
      );
    }
    
    // Predicted period start
    if (isPredictedPeriodStart(date)) {
      const daysUntil = differenceInDays(date, new Date());
      return (
        <motion.div
          className="flex flex-col items-center justify-center relative"
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 300, delay: 0.1 }}
        >
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <Droplets className="h-5 w-5 text-pink-600 fill-pink-200" />
          </motion.div>
          <motion.div
            className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center"
            animate={{ scale: [1, 1.3, 1] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <Sparkles className="w-2 h-2 text-white" />
          </motion.div>
          {daysUntil <= 7 && (
            <motion.div
              className="absolute -bottom-2 text-xs font-bold text-pink-600 bg-white rounded-full px-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil}d`}
            </motion.div>
          )}
        </motion.div>
      );
    }
    
    // Ovulation day
    if (isOvulationDay(date)) {
      return (
        <motion.div
          className="flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <Heart className="h-4 w-4 text-orange-500 fill-orange-200" />
          </motion.div>
        </motion.div>
      );
    }
    
    // Regular predicted period days
    if (isPredictedPeriod(date)) {
      return (
        <motion.div
          className="flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Droplets className="h-3 w-3 text-pink-400 fill-pink-100" />
        </motion.div>
      );
    }
    
    // Regular log indicator
    const log = getLogForDate(date);
    if (log && !isInPeriod(date) && !isPredictedPeriod(date)) {
      return <div className="h-1 w-1 rounded-full bg-purple-500 mx-auto mt-1"></div>;
    }
    
    return null;
  };

  const getDaysUntilNextPeriod = () => {
    if (!stats.nextPeriodPrediction) return null;
    const days = differenceInDays(parseISO(stats.nextPeriodPrediction), new Date());
    return days;
  };

  const daysUntilPeriod = getDaysUntilNextPeriod();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <div className="mb-8">
        <motion.h1 
          className="text-3xl font-bold text-gray-900 mb-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          Calendar View
        </motion.h1>
        
        {/* Next Period Alert */}
        {daysUntilPeriod !== null && daysUntilPeriod <= 7 && (
          <motion.div
            className={`mb-4 p-4 rounded-2xl border-2 ${
              daysUntilPeriod <= 1 
                ? 'bg-gradient-to-r from-pink-50 to-rose-50 border-pink-300' 
                : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center space-x-3">
              <motion.div
                className={`p-2 rounded-full ${
                  daysUntilPeriod <= 1 ? 'bg-pink-500' : 'bg-purple-500'
                }`}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {daysUntilPeriod <= 1 ? (
                  <AlertCircle className="w-5 h-5 text-white" />
                ) : (
                  <Clock className="w-5 h-5 text-white" />
                )}
              </motion.div>
              <div>
                <h3 className={`font-semibold ${
                  daysUntilPeriod <= 1 ? 'text-pink-900' : 'text-purple-900'
                }`}>
                  {daysUntilPeriod === 0 ? '🩸 Period Expected Today!' :
                   daysUntilPeriod === 1 ? '🩸 Period Expected Tomorrow!' :
                   `🩸 Period Expected in ${daysUntilPeriod} days`}
                </h3>
                <p className={`text-sm ${
                  daysUntilPeriod <= 1 ? 'text-pink-700' : 'text-purple-700'
                }`}>
                  {daysUntilPeriod <= 1 
                    ? 'Make sure you have supplies ready and consider logging any pre-period symptoms.'
                    : 'Look for the sparkling droplet icon on your calendar to see the predicted start date.'
                  }
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <motion.div 
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 custom-calendar"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Calendar
              onChange={setValue}
              value={value}
              className="w-full border-0"
              tileClassName={tileClassName}
              tileContent={tileContent}
              onClickDay={handleDateClick}
            />
          </motion.div>
          
          <motion.div 
            className="mt-6 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Sparkles className="w-5 h-5 text-purple-600 mr-2" />
              Calendar Legend
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-pink-500 mr-3 flex items-center justify-center">
                  <Droplets className="h-3 w-3 text-white" />
                </div>
                <span className="text-sm text-gray-700 font-medium">Period Start (Actual)</span>
              </div>
              <div className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-pink-200 mr-3"></div>
                <span className="text-sm text-gray-700">Period Day (Actual)</span>
              </div>
              <div className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-pink-300 mr-3 flex items-center justify-center relative">
                  <Droplets className="h-3 w-3 text-pink-600" />
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full">
                    <Sparkles className="w-1 h-1 text-white" />
                  </div>
                </div>
                <span className="text-sm text-gray-700 font-medium">Next Period (Predicted) ✨</span>
              </div>
              <div className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-pink-100 mr-3"></div>
                <span className="text-sm text-gray-700">Predicted Period Days</span>
              </div>
              <div className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-orange-200 mr-3 flex items-center justify-center">
                  <Heart className="h-3 w-3 text-orange-600" />
                </div>
                <span className="text-sm text-gray-700">Ovulation Day</span>
              </div>
              <div className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-green-100 mr-3"></div>
                <span className="text-sm text-gray-700">Fertile Window</span>
              </div>
              <div className="flex items-center">
                <div className="h-4 w-4 rounded-full bg-purple-100 mr-3"></div>
                <span className="text-sm text-gray-700">Logged Day</span>
              </div>
            </div>
          </motion.div>
        </div>
        
        <div>
          <motion.div 
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Plus className="w-5 h-5 text-purple-600 mr-2" />
              Quick Add
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Add a log for today or click on any date on the calendar to add a log for that day.
            </p>
            <motion.button
              onClick={() => {
                setSelectedDate(new Date());
                setSelectedLog(null);
                setShowLogForm(true);
              }}
              className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl hover:shadow-lg transition-all duration-300 font-medium"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus size={18} className="mr-2" />
              Log Today
            </motion.button>
          </motion.div>
          
          <motion.div 
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 mt-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
          >
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CalendarIcon className="w-5 h-5 text-purple-600 mr-2" />
              Cycle Information
            </h3>
            
            <div className="space-y-4">
              <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                <p className="text-sm text-purple-600 font-medium">Average Cycle Length</p>
                <p className="text-xl font-bold text-purple-900">
                  {stats.averageCycleLength} days
                </p>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl">
                <p className="text-sm text-pink-600 font-medium">Last Period</p>
                <p className="text-lg font-semibold text-pink-900">
                  {stats.lastPeriodDate 
                    ? format(parseISO(stats.lastPeriodDate), 'MMM d, yyyy')
                    : 'No data yet'}
                </p>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border-2 border-pink-200">
                <p className="text-sm text-rose-600 font-medium flex items-center">
                  <Sparkles className="w-4 h-4 mr-1" />
                  Next Period (Predicted)
                </p>
                <p className="text-lg font-bold text-rose-900">
                  {stats.nextPeriodPrediction 
                    ? format(parseISO(stats.nextPeriodPrediction), 'MMM d, yyyy')
                    : 'No data yet'}
                </p>
                {daysUntilPeriod !== null && (
                  <p className="text-xs text-rose-700 mt-1">
                    {daysUntilPeriod === 0 ? 'Expected today!' :
                     daysUntilPeriod === 1 ? 'Expected tomorrow!' :
                     daysUntilPeriod > 0 ? `In ${daysUntilPeriod} days` :
                     'Overdue - consider logging if started'}
                  </p>
                )}
              </div>

              {ovulationDate && (
                <div className="p-3 bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl">
                  <p className="text-sm text-orange-600 font-medium flex items-center">
                    <Heart className="w-4 h-4 mr-1" />
                    Next Ovulation
                  </p>
                  <p className="text-lg font-semibold text-orange-900">
                    {format(ovulationDate, 'MMM d, yyyy')}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Log Form Modal */}
      <AnimatePresence>
        {showLogForm && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseForm}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/20"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  {selectedLog 
                    ? 'Edit Log' 
                    : selectedDate 
                      ? `Add Log - ${format(selectedDate, 'MMMM d, yyyy')}` 
                      : 'Add Log'}
                </h2>
                <CycleLogForm
                  logId={selectedLog}
                  selectedDate={selectedDate || undefined}
                  onClose={handleCloseForm}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <style jsx>{`
        .custom-calendar .period-start {
          background: linear-gradient(135deg, #ec4899, #be185d) !important;
          color: white !important;
          border-radius: 50%;
          position: relative;
        }
        .custom-calendar .period-day {
          background: rgba(236, 72, 153, 0.3) !important;
          border-radius: 8px;
        }
        .custom-calendar .predicted-period-start {
          background: linear-gradient(135deg, #f9a8d4, #ec4899) !important;
          color: #be185d !important;
          border-radius: 50%;
          border: 2px solid #ec4899 !important;
          position: relative;
          animation: pulse 2s infinite;
        }
        .custom-calendar .predicted-period {
          background: rgba(236, 72, 153, 0.15) !important;
          border: 1px dashed #ec4899 !important;
          border-radius: 8px;
        }
        .custom-calendar .ovulation-day {
          background: linear-gradient(135deg, #fed7aa, #fb923c) !important;
          color: #ea580c !important;
          border-radius: 50%;
          border: 2px solid #fb923c !important;
        }
        .custom-calendar .fertile-window {
          background: rgba(34, 197, 94, 0.1) !important;
          border: 1px solid #22c55e !important;
          border-radius: 8px;
        }
        .custom-calendar .has-log {
          background: rgba(168, 85, 247, 0.1) !important;
        }
        .custom-calendar .react-calendar__tile--now {
          background: rgba(168, 85, 247, 0.2) !important;
          font-weight: bold;
        }
        .custom-calendar .react-calendar__tile--active {
          background: rgba(168, 85, 247, 0.5) !important;
          color: white !important;
        }
        .custom-calendar .react-calendar__tile {
          position: relative;
          min-height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          transition: all 0.3s ease;
        }
        .custom-calendar .react-calendar__tile:hover {
          background: rgba(168, 85, 247, 0.1) !important;
          transform: scale(1.05);
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </motion.div>
  );
};

export default CalendarView;